import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Play, Cpu, Workflow, Network, Factory, Compass, Activity, Shield, Award, Users, Zap } from "lucide-react";
import { HERO, STATS, SERVICES, CLIENTS, PROJECTS, TESTIMONIALS, INDUSTRIES } from "../data/site";
import Reveal from "../components/Reveal";

const ICONS = { Cpu, Workflow, Network, Factory, Compass, Activity };

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden flex flex-col">
        <video
          data-testid="hero-video"
          autoPlay loop muted playsInline
          poster={HERO.poster}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO.videoMp4} type="video/mp4" />
          <source src={HERO.videoMp4} type="video/x-m4v" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="grid-bg absolute inset-0 opacity-40" />

        <div className="relative z-10 container-wide flex-1 flex flex-col justify-end pt-32 md:pt-36 pb-12 md:pb-16">
          <div className="max-w-5xl">
            <div className="eyebrow eyebrow-light mb-6 md:mb-8" data-testid="hero-eyebrow">
              <span className="pulse-dot inline-block mr-1" /> Australian engineering · Since 1983
            </div>
            <h1 className="font-display text-white text-[clamp(36px,6.5vw,112px)] leading-[0.95] tracking-[-0.04em]">
              Engineering<br />
              the systems that<br />
              <span className="text-[var(--primary)]">run Australia.</span>
            </h1>
            <p className="text-white/75 text-base md:text-xl max-w-2xl mt-6 md:mt-10 leading-relaxed font-light">
              UMD designs, manufactures and deploys mission-critical IoT, edge and engineering solutions across infrastructure, government, manufacturing and defence — locally built in Melbourne, deployed nationally.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8 md:mt-12">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                Discuss your project <ArrowUpRight size={18} className="arrow" />
              </Link>
              <Link to="/projects" data-testid="hero-cta-secondary" className="btn-outline btn-outline-light">
                <Play size={14} /> View our work
              </Link>
            </div>
          </div>
        </div>

        {/* Stat strip - inside hero, full-width along bottom */}
        <div className="relative z-10 container-wide pb-10 md:pb-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15">
            {STATS.map((s, i) => (
              <div key={i} className="bg-[#0B1B3D]/65 backdrop-blur-md px-5 md:px-6 py-5 md:py-6">
                <div className="font-display text-white text-2xl md:text-4xl tracking-tight">{s.value}</div>
                <div className="text-white/70 text-[11px] md:text-xs mt-2 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* corner mono labels */}
        <div className="absolute top-28 right-8 hidden md:flex flex-col items-end gap-2 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40 z-10">
          <span>37.9°S · 145.0°E</span>
          <span>Melbourne · Bentleigh East</span>
          <span className="text-[var(--primary)]">UMD/0001 — LIVE</span>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section data-testid="clients-section" className="py-14 border-y border-[var(--border)] bg-white">
        <div className="container-wide flex items-center gap-12">
          <div className="hidden md:block shrink-0 w-48">
            <div className="text-[10px] font-mono-tech tracking-[0.25em] uppercase text-[var(--muted-fg)]">Trusted by</div>
            <div className="text-sm font-semibold mt-1">300+ Australian organisations</div>
          </div>
          <div className="marquee flex-1">
            <div className="marquee-track">
              {[...CLIENTS, ...CLIENTS].map((c, i) => (
                <span key={i} className="font-display text-2xl md:text-3xl tracking-tight text-[#999] hover:text-[#0a0a0a] transition-colors whitespace-nowrap">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="py-24 md:py-32 bg-white relative">
        <div className="container-wide grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5 md:sticky md:top-32">
            <div className="eyebrow mb-6">About UMD</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
              We don't sell hardware.<br />
              We solve problems<br />
              with <span className="text-[var(--primary)]">engineering.</span>
            </h2>
          </Reveal>
          <Reveal className="md:col-span-6 md:col-start-7" delay={150}>
            <p className="text-lg md:text-xl leading-relaxed text-[#222] font-light">
              Since 1983, UMD has been the trusted engineering partner behind some of Australia's most demanding industrial deployments. We design and manufacture IoT devices, integrate complex enterprise systems, and provide engineering services that span electronics, IT and software.
            </p>
            <p className="text-base leading-relaxed text-[var(--muted-fg)] mt-6">
              We are passionate about creating innovative solutions for organisations who understand that real technology innovation drives competitive advantage. Once we have a client, we embed into their team and stay there — that's why our average client relationship is over eleven years.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-px bg-[var(--border)]">
              <Stat label="Years" value="40+" />
              <Stat label="Engineers" value="120" />
              <Stat label="Projects" value="1,200+" />
            </div>
            <Link to="/about" data-testid="about-readmore" className="btn-ghost mt-10">Read our story <ArrowRight size={14}/></Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#fafafa] relative">
        <div className="grid-bg-light absolute inset-0 pointer-events-none" />
        <div className="container-wide relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="eyebrow mb-6">Capabilities</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
                Six disciplines.<br />One engineering team.
              </h2>
            </div>
            <Link to="/services" data-testid="services-viewall" className="btn-ghost shrink-0">All services <ArrowRight size={14}/></Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="svc-card bg-white p-10 group flex flex-col gap-5 min-h-[340px]"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono-tech text-[11px] tracking-[0.2em] text-[var(--muted-fg)] group-hover:text-[var(--primary)]">{s.code}</span>
                    <Icon size={32} className="svc-icon" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">{s.title}</h3>
                  <p className="text-sm text-[var(--muted-fg)] group-hover:text-white/70 leading-relaxed flex-1">{s.short}</p>
                  <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.12em] uppercase mt-2">
                    Explore <ArrowUpRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS FEATURED */}
      <section data-testid="projects-section" className="py-24 md:py-32 bg-white">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="eyebrow mb-6">Selected work</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
                Mission-critical, delivered.
              </h2>
            </div>
            <Link to="/projects" data-testid="projects-viewall" className="btn-ghost shrink-0">All projects <ArrowRight size={14}/></Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            {PROJECTS.slice(0, 4).map((p, i) => (
              <Reveal key={p.slug} delay={i * 80} className={i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-4"}>
                <Link to="/projects" data-testid={`project-card-${p.slug}`} className="proj-card block aspect-[4/3] md:aspect-auto md:h-[420px] relative">
                  <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="proj-mask absolute inset-0" />
                  <div className="absolute top-5 left-5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/80 bg-black/30 backdrop-blur-md px-3 py-1.5">
                    {p.category} · {p.year}
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 className="font-display text-xl md:text-2xl leading-tight tracking-tight">{p.title}</h3>
                    <div className="mt-2 text-xs text-white/70 font-mono-tech">{p.location}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES STRIP */}
      <section data-testid="industries-section" className="py-24 md:py-32 bg-[#08142b] text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-60" />
        <div className="container-wide relative">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-6">
              <div className="eyebrow eyebrow-light mb-6">Industries</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
                Where UMD<br />works.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-white/70 text-base leading-relaxed">
                Critical industries demand uncompromising engineering. From rail networks to defence facilities, from manufacturing floors to research institutions — we partner with operators who can't afford failure.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10">
            {INDUSTRIES.slice(0, 9).map((ind, i) => (
              <Link key={ind.name} to="/industries" data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`} className="bg-[#08142b] hover:bg-[#0e1f4a] transition-colors p-6 md:p-8 group">
                <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[var(--primary)] mb-3">0{i + 1}</div>
                <div className="font-display text-xl md:text-2xl leading-tight tracking-tight group-hover:translate-x-1 transition-transform">{ind.name}</div>
                <div className="text-xs text-white/50 mt-2">{ind.count}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-white">
        <div className="container-wide">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-7">
              <div className="eyebrow mb-6">Why UMD</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
                Built to outlast<br />the project.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
            {[
              { icon: Award, title: "Locally engineered", text: "Sovereign Australian engineering. Designed, prototyped and manufactured in Melbourne." },
              { icon: Shield, title: "Compliance-first", text: "ISO 9001, ISO 27001 and AS/NZS aligned across every project we deliver." },
              { icon: Zap, title: "Edge to enterprise", text: "From the silicon up to the dashboard — a single team, a single point of accountability." },
              { icon: Users, title: "Embedded partners", text: "We don't ship and leave. UMD becomes part of your engineering team for the long term." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 80} className="bg-white p-8 md:p-10">
                <f.icon size={36} strokeWidth={1.4} className="text-[var(--primary)] mb-6" />
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{f.title}</h3>
                <p className="text-sm text-[var(--muted-fg)] mt-4 leading-relaxed">{f.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section data-testid="testimonial-section" className="py-24 md:py-36 bg-[#0a0a0a] text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="container-wide relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-2">
            <div className="font-display text-[120px] leading-none text-[var(--primary)]">"</div>
          </div>
          <div className="md:col-span-10">
            <p className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight max-w-5xl">
              {TESTIMONIALS[0].quote}
            </p>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-12 bg-[var(--primary)] flex items-center justify-center font-display text-lg">{TESTIMONIALS[0].name[0]}</div>
              <div>
                <div className="font-semibold">{TESTIMONIALS[0].name}</div>
                <div className="text-sm text-white/50 font-mono-tech tracking-wider">{TESTIMONIALS[0].role} · {TESTIMONIALS[0].company}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#fafafa] relative overflow-hidden">
        <div className="container-wide grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow mb-6">Start a conversation</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
              The next critical<br />
              deployment starts<br />
              with a <span className="text-[var(--primary)]">five-minute call.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4">
            <Link to="/contact" data-testid="final-cta-call" className="btn-primary justify-between">Book a consultation <ArrowUpRight size={18} className="arrow"/></Link>
            <Link to="/services" data-testid="final-cta-services" className="btn-outline justify-between">Explore services <ArrowRight size={16}/></Link>
          </div>
        </div>
      </section>
    </>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white p-6">
    <div className="font-display text-3xl md:text-4xl tracking-tight">{value}</div>
    <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[var(--muted-fg)] mt-2">{label}</div>
  </div>
);
